// 📄 pages/Proyectos/components/TareaDetailModal.tsx
// Detalle de una tarea: cambiar estado, ver/agregar comentarios, editar
// (no disponible para "cliente", ver firestore.rules — un cliente solo
// puede tocar estado/comentarios de SU PROPIA tarea asignada).
import { useState } from 'react';
import { X, Pencil, Send, RefreshCw, Calendar } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import {
  Tarea, TareaEstado, ESTADOS_TAREA, updateTareaEstado, addComentarioTarea, estaVencida
} from '../../../services/tareaService.ts';

interface TareaDetailModalProps {
  projectId: string;
  tarea: Tarea;
  esCliente: boolean;
  onClose: () => void;
  onUpdated: () => void;
  onEditar: () => void;
}

const ESTADO_STYLES: Record<TareaEstado, string> = {
  'Pendiente': 'text-gray-300 bg-white/5 border-white/10',
  'En progreso': 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  'Completada': 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
};

export default function TareaDetailModal({ projectId, tarea, esCliente, onClose, onUpdated, onEditar }: TareaDetailModalProps) {
  const { user } = useAuth();
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  const [comentario, setComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const esPropiaTarea = user?.uid === tarea.asignadoA;
  // Cliente solo puede tocar estado/comentarios de su propia tarea (ver
  // firestore.rules) — se oculta el control en vez de mostrar una acción
  // que fallaría con permission-denied.
  const puedeCambiarEstado = !esCliente || esPropiaTarea;
  const puedeEditar = !esCliente;

  const handleCambiarEstado = async (estado: TareaEstado) => {
    if (estado === tarea.estado) return;
    setCambiandoEstado(true);
    setError(null);
    try {
      await updateTareaEstado(projectId, tarea.id, estado, tarea.hitoId || '');
      onUpdated();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar el estado.');
    } finally {
      setCambiandoEstado(false);
    }
  };

  const handleComentar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !comentario.trim()) return;
    setEnviandoComentario(true);
    setError(null);
    try {
      await addComentarioTarea(projectId, tarea.id, user.email || user.uid, user.uid, comentario);
      setComentario('');
      onUpdated();
    } catch (err: any) {
      setError(err.message || 'Error al agregar el comentario.');
    } finally {
      setEnviandoComentario(false);
    }
  };

  const vencida = estaVencida(tarea);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{tarea.titulo}</h3>
          <div className="flex items-center gap-1">
            {puedeEditar && (
              <button onClick={onEditar} className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg" title="Editar">
                <Pencil className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className={`px-2 py-0.5 rounded-full border font-semibold ${vencida ? 'text-red-300 bg-red-500/10 border-red-500/30' : 'text-gray-400 bg-white/5 border-white/10'}`}>
              <Calendar className="w-3 h-3 inline mr-1" /> {tarea.fechaLimite}{vencida ? ' · Vencida' : ''}
            </span>
            <span className="px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
              Asignado: {tarea.asignadoANombre}
            </span>
            {tarea.seccionRelacionada && (
              <span className="px-2 py-0.5 rounded-full border text-gray-300 bg-white/5 border-white/10">{tarea.seccionRelacionada}</span>
            )}
          </div>

          {tarea.descripcion && <p className="text-sm text-gray-300 whitespace-pre-wrap">{tarea.descripcion}</p>}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Estado</label>
            <div className="flex gap-1.5">
              {ESTADOS_TAREA.map((e) => (
                <button
                  key={e}
                  type="button"
                  disabled={!puedeCambiarEstado || cambiandoEstado}
                  onClick={() => handleCambiarEstado(e)}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    tarea.estado === e ? ESTADO_STYLES[e] : 'text-gray-500 bg-transparent border-transparent hover:bg-white/5'
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t border-white/10 space-y-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Comentarios</p>
            {tarea.comentarios.length === 0 ? (
              <p className="text-xs text-gray-500">Todavía no hay comentarios.</p>
            ) : (
              <div className="space-y-2.5">
                {tarea.comentarios.map((c, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3">
                    <p className="text-xs font-semibold text-gray-300">{c.autor}</p>
                    <p className="text-sm text-gray-300 mt-0.5 whitespace-pre-wrap">{c.texto}</p>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleComentar} className="flex items-center gap-2">
              <input
                type="text"
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Agregar un comentario..."
                className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
              />
              <button
                type="submit"
                disabled={enviandoComentario || !comentario.trim()}
                className="flex items-center justify-center bg-blue-600 hover:bg-blue-500 disabled:opacity-50 w-9 h-9 rounded-lg text-white transition-all flex-shrink-0"
              >
                {enviandoComentario ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
